import { TFunction } from 'i18next';
import { HeaderField, TechnicalVisitPreventorUser } from '../types/technicalVisit.types';
import { getBooleanLabel, getEmployeeCount, getValue } from '../utils/technicalVisitUtils';

type UseTechnicalVisitDataParams = {
    selectedVisita: any;
    establecimiento: any;
    preventorUser?: TechnicalVisitPreventorUser;
    t: TFunction;
};

export const useTechnicalVisitData = ({
    selectedVisita,
    establecimiento,
    preventorUser,
    t
}: UseTechnicalVisitDataParams) => {
    const establishmentName =
        selectedVisita?.nombreEstablecimiento || establecimiento?.nombre || 'N/A';

    const organismo = establecimiento?.organismo || {};
    const organismoCentral = organismo?.organismoCentral || {};
    const preventor = selectedVisita?.preventor;
    const preventorProvider =
        preventorUser?.preventor?.proveedor || preventorUser?.proveedor || preventor?.proveedor;
    const preventorName = `${preventorUser?.nombre || preventor?.nombre || ''} ${
        preventorUser?.apellido || preventor?.apellido || ''
    }`.trim();

    const contractFields: HeaderField[] = [
        {
            label: t('technicalVisit.fields.businessName'),
            value: getValue(organismoCentral?.nombre),
            lg: 1.8
        },
        {
            label: t('technicalVisit.fields.contractCuit'),
            value: getValue(organismoCentral?.cuit),
            lg: 1.4
        },
        {
            label: t('technicalVisit.fields.contractNumber'),
            value: getValue(organismoCentral?.contrato),
            lg: 1.2
        },
        {
            label: t('technicalVisit.fields.contractCiiu'),
            value: getValue(organismoCentral?.ciiu?.codigo),
            lg: 1.2
        },
        {
            label: t('technicalVisit.fields.contractEmail'),
            value: getValue(organismoCentral?.email),
            lg: 1.6
        },
        {
            label: t('technicalVisit.fields.phone'),
            value: getValue(organismoCentral?.telefono),
            lg: 1.2
        },
        {
            label: t('technicalVisit.fields.organismName'),
            value: getValue(organismo?.nombre),
            lg: 1.8
        },
        {
            label: t('technicalVisit.fields.organismCuit'),
            value: getValue(organismo?.cuit),
            lg: 1.4
        }
    ];

    const establishmentFields: HeaderField[] = [
        {
            label: t('technicalVisit.fields.establishmentNumber'),
            value: getValue(establecimiento?.codigoSRT),
            lg: 1.7
        },
        {
            label: t('technicalVisit.fields.ciiu'),
            value: getValue(
                establecimiento?.ciiu?.codigo && establecimiento?.ciiu?.descripcion
                    ? `${establecimiento.ciiu.codigo} - ${establecimiento.ciiu.descripcion}`
                    : establecimiento?.ciiu?.codigo
            ),
            lg: 1.8
        },
        {
            label: t('technicalVisit.fields.province'),
            value: getValue(establecimiento?.direccion?.localidad?.provincia?.nombre),
            lg: 1.2
        },
        {
            label: t('technicalVisit.fields.street'),
            value: getValue(establecimiento?.direccion?.calle),
            lg: 1.6
        },
        {
            label: t('technicalVisit.fields.streetNumber'),
            value: getValue(establecimiento?.direccion?.altura),
            lg: 1.1
        },
        {
            label: t('technicalVisit.fields.city'),
            value: getValue(establecimiento?.direccion?.localidad?.nombre),
            lg: 1.4
        },
        {
            label: t('technicalVisit.fields.postalCode'),
            value: getValue(establecimiento?.direccion?.localidad?.codigoPostal),
            lg: 1.1
        },
        {
            label: t('technicalVisit.fields.floor'),
            value: getValue(establecimiento?.direccion?.piso),
            lg: 0.8
        },
        {
            label: t('technicalVisit.fields.apartment'),
            value: getValue(establecimiento?.direccion?.departamento),
            lg: 0.8
        },
        {
            label: t('technicalVisit.fields.employeeCount'),
            value: getEmployeeCount(establecimiento),
            lg: 1.4
        }
    ];

    const responsibleFields: HeaderField[] = [
        { label: t('technicalVisit.fields.fullName'), value: preventorName || 'N/A', lg: 1.8 },
        {
            label: t('technicalVisit.fields.taxId'),
            value: getValue(preventorUser?.cuil || preventorProvider?.cuit),
            lg: 1.7
        },
        {
            label: t('technicalVisit.fields.role'),
            value: t('technicalVisit.values.preventor'),
            lg: 1.2
        },
        {
            label: t('technicalVisit.fields.ownOrContracted'),
            value: getBooleanLabel(preventorUser?.factura ?? preventorProvider?.factura),
            lg: 1.6
        },
        {
            label: t('technicalVisit.fields.qualifyingDegree'),
            value: getValue(
                preventorUser?.tituloHabilitante || preventorProvider?.tituloHabilitante
            ),
            lg: 1.6
        },
        {
            label: t('technicalVisit.fields.licenseNumber'),
            value: getValue(preventorUser?.matricula || preventorProvider?.matricula),
            lg: 1.4
        },
        { label: t('technicalVisit.fields.grantingEntity'), value: 'N/A', lg: 1.6 }
    ];

    return {
        establishmentName,
        contractFields,
        establishmentFields,
        responsibleFields
    };
};
